import {
  ConditionalEntry,
  DoseEvent,
  MedicineTimingRuleType,
  PillboxItem,
  ScheduleDay,
} from "@/types/pillbox";

// Client-side port of the API's DoseEventGeneratorService.generate() (see
// api/src/modules/pillbox/services/dose-event-generator.service.ts) — kept
// deliberately identical in logic so the interactive Schedule view (computed
// here) and the PDF/WhatsApp share (still computed server-side, since that's
// generated outside any client session) always agree on what a given day's
// schedule looks like.
//
// Runs entirely against the member's already-fetched active ledger — no
// network call per date, so navigating to any day (past or future) is free.

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Parses a "YYYY-MM-DD" key as UTC midnight — unambiguous regardless of the
// browser's local timezone, matching how the backend interprets the same
// date strings (it always worked in UTC days; the client just forwarded a
// locally-computed date string that got parsed as UTC on arrival).
function parseDateKey(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

function startOfUtcDay(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
}

function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * ONE_DAY_MS);
}

function toDateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatHHmm(d: Date): string {
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

function buildDose(
  item: PillboxItem,
  dateKey: string,
  time: string
): DoseEvent {
  return {
    id: `${item.id}-${dateKey}-${time}`,
    memberId: item.memberId,
    itemType: item.itemType,
    itemId: item.id,
    date: dateKey,
    time,
    name: item.name,
    dose: item.dose,
    notes: item.notes,
    route: item.route,
  };
}

function generateFixedSlots(
  item: PillboxItem,
  windowStart: Date,
  windowEnd: Date
): DoseEvent[] {
  const slots = item.slots ?? [];
  if (slots.length === 0 || !item.startDate) return [];
  const weekdays = item.selectedWeekdays ?? [];
  const useWeekdays = weekdays.length > 0;
  const intervalDays = item.recurrenceIntervalDays ?? 1;
  const anchorDay = startOfUtcDay(new Date(item.startDate));
  const rows: DoseEvent[] = [];
  for (
    let d = windowStart;
    d.getTime() <= windowEnd.getTime();
    d = addDays(d, 1)
  ) {
    if (useWeekdays) {
      if (!weekdays.includes(d.getUTCDay())) continue;
    } else {
      const dayIndex = Math.round(
        (d.getTime() - anchorDay.getTime()) / ONE_DAY_MS
      );
      // dayIndex can be negative if the window start precedes startDate;
      // use a positive modulo so the cadence still anchors correctly.
      if (((dayIndex % intervalDays) + intervalDays) % intervalDays !== 0)
        continue;
    }
    const dateKey = toDateKey(d);
    for (const time of slots) {
      rows.push(buildDose(item, dateKey, time));
    }
  }
  return rows;
}

function generateFixedInterval(
  item: PillboxItem,
  windowStart: Date,
  windowEnd: Date
): DoseEvent[] {
  if (!item.intervalHours || item.intervalHours <= 0 || !item.startDate)
    return [];
  const stepMs = item.intervalHours * 60 * 60 * 1000;
  const anchor = (
    item.intervalStartTime
      ? new Date(item.intervalStartTime)
      : new Date(item.startDate)
  ).getTime();

  let t = anchor;
  if (t < windowStart.getTime()) {
    const periods = Math.ceil((windowStart.getTime() - t) / stepMs);
    t += periods * stepMs;
  }

  const windowEndExclusive = windowEnd.getTime() + ONE_DAY_MS;
  const rows: DoseEvent[] = [];
  while (t < windowEndExclusive) {
    const dt = new Date(t);
    const dateKey = toDateKey(startOfUtcDay(dt));
    rows.push(buildDose(item, dateKey, formatHHmm(dt)));
    t += stepMs;
  }
  return rows;
}

function generateDosesForItem(
  item: PillboxItem,
  rangeStart: Date,
  rangeEnd: Date
): DoseEvent[] {
  if (item.timingRuleType === MedicineTimingRuleType.CONDITIONAL) return [];
  if (!item.startDate) return [];

  const itemStart = startOfUtcDay(new Date(item.startDate));
  const windowStart = new Date(
    Math.max(rangeStart.getTime(), itemStart.getTime())
  );
  let windowEnd = rangeEnd;
  if (item.endDate) {
    const end = startOfUtcDay(new Date(item.endDate));
    if (end.getTime() < windowEnd.getTime()) windowEnd = end;
  }
  if (windowEnd.getTime() < windowStart.getTime()) return [];

  return item.timingRuleType === MedicineTimingRuleType.FIXED_SLOTS
    ? generateFixedSlots(item, windowStart, windowEnd)
    : generateFixedInterval(item, windowStart, windowEnd);
}

/**
 * Computes ScheduleDay[] for the given calendar date-keys ("YYYY-MM-DD",
 * ascending) from the member's already-fetched active ledger. Mirrors
 * PillboxScheduleService.getSchedule() exactly.
 */
export function computeSchedule(
  ledger: PillboxItem[],
  dateKeys: string[]
): ScheduleDay[] {
  if (dateKeys.length === 0) return [];
  const rangeStart = parseDateKey(dateKeys[0]);
  const rangeEnd = parseDateKey(dateKeys[dateKeys.length - 1]);

  const conditionalItems = ledger.filter(
    (item) => item.timingRuleType === MedicineTimingRuleType.CONDITIONAL
  );

  const eventsByDay = new Map<string, DoseEvent[]>();
  for (const item of ledger) {
    if (item.timingRuleType === MedicineTimingRuleType.CONDITIONAL) continue;
    for (const row of generateDosesForItem(item, rangeStart, rangeEnd)) {
      const list = eventsByDay.get(row.date) ?? [];
      list.push(row);
      eventsByDay.set(row.date, list);
    }
  }

  return dateKeys.map((key) => {
    const d = parseDateKey(key);
    const doses = (eventsByDay.get(key) ?? []).sort((a, b) =>
      a.time.localeCompare(b.time)
    );
    const conditional: ConditionalEntry[] = conditionalItems
      .filter(
        (item) =>
          item.startDate &&
          d.getTime() >= startOfUtcDay(new Date(item.startDate)).getTime()
      )
      .map((item) => ({
        id: item.id,
        itemType: item.itemType,
        name: item.name,
        dose: item.dose,
        notes: item.notes,
      }));
    return { date: key, doses, conditional };
  });
}
